import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ClipboardList, Camera, MapPin, X, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  OPEN: { label: "Diajukan", className: "bg-red-500/10 text-red-500 border-red-500/20" },
  IN_PROGRESS: { label: "Dikerjakan", className: "bg-blue-500/10 text-blue-500 border-blue-500/20" },
  COMPLETED: { label: "Selesai", className: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" },
  CANCELLED: { label: "Dibatalkan", className: "bg-slate-500/10 text-slate-500 border-slate-500/20" },
};
const REASONS = { DUPLICATE: "Duplicate / WO ganda", CREATED_BY_MISTAKE: "Salah dibuat", OTHER: "Lainnya" };
const HISTORY_ACTIONS = { CREATED: "Dibuat", EDITED: "Dikoreksi", CANCELLED: "Dibatalkan", STARTED: "Mulai dikerjakan", COMPLETED: "Diselesaikan" };
const FIELD_LABELS = { location: "Lokasi", title: "Judul", description: "Deskripsi", images: "Foto" };
const formatSubmittedAt = (value) => {
  if (!value || Number.isNaN(new Date(value).getTime())) return "Waktu pengajuan tidak tersedia";
  return `${new Intl.DateTimeFormat("id-ID", {
    timeZone: "Asia/Jakarta", day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).format(new Date(value))} WIB`;
};

const WorkOrders = () => {
  const { user } = useAuth();
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [actionState, setActionState] = useState({}); // { [wo_id]: { pic_name, completion_notes, completion_images } }
  const [acting, setActing] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [correction, setCorrection] = useState(null);
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const requestId = useRef(0);

  const [form, setForm] = useState({ location: "", title: "", description: "", images: [] });

  const isHod = user?.role === "HOD";
  const isEng = isHod && user?.department === "ENG";
  const canCreate = isHod;
  const canManage = isEng || ["DM", "GM"].includes(user?.role);
  const canEdit = (wo) => wo.status !== "CANCELLED" &&
    (["DM", "GM"].includes(user?.role) || (isHod && wo.requested_by === user?.id && wo.status === "OPEN"));
  const canCancel = (wo) => ["OPEN", "IN_PROGRESS"].includes(wo.status) &&
    (["DM", "GM"].includes(user?.role) || (isHod && wo.requested_by === user?.id && wo.status === "OPEN"));

  const openCorrection = (wo, mode) => setCorrection({
    id: wo.id, mode, revision: wo.revision || 0, location: wo.location, title: wo.title,
    description: wo.description || "", images: [...(wo.images || [])],
    reason: "", reason_code: "DUPLICATE", duplicate_of: "",
  });

  const saveCorrection = async () => {
    const draft = correction;
    if (!draft.reason.trim() || (draft.mode === "edit" && (!draft.title.trim() || !draft.location.trim())) ||
        (draft.mode === "cancel" && draft.reason_code === "DUPLICATE" && !draft.duplicate_of.trim())) {
      toast.error("Lengkapi kolom wajib dan alasan perubahan.");
      return;
    }
    setActing(draft.id);
    try {
      const payload = draft.mode === "edit" ? {
        location: draft.location.trim(), title: draft.title.trim(), description: draft.description.trim() || null,
        images: draft.images, reason: draft.reason.trim(), revision: draft.revision,
      } : {
        reason_code: draft.reason_code, reason: draft.reason.trim(), revision: draft.revision,
        duplicate_of: draft.reason_code === "DUPLICATE" ? draft.duplicate_of.trim().toUpperCase() : null,
      };
      await axios.put(`${API}/work-orders/${draft.id}${draft.mode === "cancel" ? "/cancel" : ""}`, payload);
      toast.success(draft.mode === "edit" ? "Koreksi WO tersimpan" : "WO dibatalkan, riwayat tetap tersimpan");
      setCorrection(null);
      await fetchWorkOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Perubahan WO gagal disimpan");
      if (err.response?.status === 409) {
        setCorrection(null);
        await fetchWorkOrders();
      }
    } finally {
      setActing(null);
    }
  };

  const fetchWorkOrders = useCallback(async () => {
    const currentRequest = ++requestId.current;
    try {
      setLoading(true);
      const suffix = searchQuery ? `?${new URLSearchParams({ q: searchQuery })}` : "";
      const res = await axios.get(`${API}/work-orders${suffix}`);
      if (currentRequest === requestId.current) setWorkOrders(res.data);
    } catch (err) {
      if (currentRequest === requestId.current) toast.error("Failed to load Work Orders");
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchWorkOrders();
    return () => { requestId.current += 1; };
  }, [fetchWorkOrders]);

  const handleFileUpload = async (file, onDone) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onDone(res.data.url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.location.trim() || !form.title.trim()) {
      toast.error("Location and title are required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/work-orders`, {
        location: form.location.trim(),
        title: form.title.trim(),
        description: form.description.trim() || null,
        images: form.images,
      });
      toast.success("Work Order submitted");
      setForm({ location: "", title: "", description: "", images: [] });
      fetchWorkOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit Work Order");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStart = async (id) => {
    setActing(id);
    try {
      await axios.put(`${API}/work-orders/${id}/start`, {
        pic_name: actionState[id]?.pic_name || null,
      });
      toast.success("Work Order started");
      fetchWorkOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start Work Order");
    } finally {
      setActing(null);
    }
  };

  const handleComplete = async (id) => {
    setActing(id);
    try {
      await axios.put(`${API}/work-orders/${id}/complete`, {
        completion_notes: actionState[id]?.completion_notes || null,
        completion_images: actionState[id]?.completion_images || [],
      });
      toast.success("Work Order completed");
      fetchWorkOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to complete Work Order");
    } finally {
      setActing(null);
    }
  };

  const setAction = (id, patch) => {
    setActionState((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  };

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-6">
        <div>
          <h1 className="font-heading text-2xl uppercase tracking-wider flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-orange-500" />
            Work Orders
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Permintaan kerja ke Engineering - bukan laporan defect tamu
          </p>
        </div>

        {canCreate && (
          <Card className="orbit-card">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-sm uppercase tracking-wider">
                New Work Order Request
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Location</label>
                <input
                  type="text"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="e.g. Eateria Restaurant, Gudang, Kamar 5201"
                  className="w-full px-3 py-2 text-sm rounded-sm border border-border bg-background"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Job Title</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Perbaikan water heater"
                  className="w-full px-3 py-2 text-sm rounded-sm border border-border bg-background"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Description (optional)</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Detail kerjaan..."
                  rows={3}
                  className="w-full px-3 py-2 text-sm rounded-sm border border-border bg-background"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Photo (optional)</label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    id="wo-new-photo"
                    className="hidden"
                    onChange={(e) => handleFileUpload(e.target.files?.[0], (url) => setForm((prev) => ({ ...prev, images: [...prev.images, url] })))}
                  />
                  <label
                    htmlFor="wo-new-photo"
                    className="text-[10px] px-2.5 py-1.5 rounded-sm border border-border text-muted-foreground cursor-pointer flex items-center gap-1.5"
                  >
                    <Camera size={11} /> {uploading ? "Uploading..." : "Add Photo"}
                  </label>
                  {form.images.map((url, idx) => (
                    <img
                      key={idx}
                      src={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                      alt="attachment"
                      className="w-10 h-10 object-cover rounded border border-border cursor-pointer hover:opacity-80"
                      onClick={() => setSelectedImage(`${process.env.REACT_APP_BACKEND_URL}${url}`)}
                    />
                  ))}
                </div>
              </div>
              <Button onClick={handleSubmit} disabled={submitting} className="w-full mt-2">
                {submitting ? "Submitting..." : "Submit Work Order"}
              </Button>
            </CardContent>
          </Card>
        )}

        <Card className="orbit-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-sm uppercase tracking-wider">
              {canManage ? "Work Order Queue" : "My Work Orders"} ({workOrders.length})
            </CardTitle>
            <label className="block text-xs text-muted-foreground">
              Cari Work Order
              <div className="relative mt-1">
                <Search size={16} className="absolute left-3 top-2.5 pointer-events-none" />
                <input type="search" value={search} maxLength={200}
                  onChange={(e) => setSearch(e.target.value)} aria-label="Cari Work Order"
                  placeholder="Nomor WO, judul, lokasi, deskripsi, pengaju, atau PIC..."
                  className="w-full pl-9 pr-3 py-2 text-sm rounded-sm border border-border bg-background" />
              </div>
            </label>
            <p className="text-xs text-muted-foreground">
              {searchQuery ? `Hasil pencarian: ${workOrders.length} WO. ` : ""}
              Menampilkan maksimal 500 WO terbaru{searchQuery ? " yang cocok" : ""}. Pencarian mencakup WO lama sesuai hak akses.
            </p>
            <p className="text-xs text-muted-foreground">
              Aktif: {workOrders.filter((wo) => ["OPEN", "IN_PROGRESS"].includes(wo.status)).length}
              {" · "}Selesai: {workOrders.filter((wo) => wo.status === "COMPLETED").length}
              {" · "}Dibatalkan: {workOrders.filter((wo) => wo.status === "CANCELLED").length}
            </p>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-muted-foreground text-center py-6">Loading...</p>
            ) : workOrders.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">{searchQuery ? "Tidak ada WO yang cocok. Coba kata kunci lain." : "No Work Orders yet"}</p>
            ) : (
              <div className="space-y-2">
                {workOrders.map((wo) => {
                  const isExpanded = expandedId === wo.id;
                  const badge = STATUS_BADGE[wo.status] || STATUS_BADGE.OPEN;
                  return (
                    <div key={wo.id} className="rounded-sm border border-border">
                      <div
                        className="flex items-center justify-between gap-3 p-3 cursor-pointer"
                        onClick={() => setExpandedId(isExpanded ? null : wo.id)}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-medium text-amber-500">{wo.wo_number}</span>
                            <Badge className={`text-[10px] border ${badge.className}`}>{badge.label}</Badge>
                          </div>
                          <p className="text-sm font-medium mt-1">{wo.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                            <MapPin size={10} /> {wo.location} · by {wo.requested_by_name}
                            {wo.requested_by_department ? ` (${wo.requested_by_department})` : ""}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Diajukan: <time dateTime={wo.created_at || undefined}>{formatSubmittedAt(wo.created_at)}</time>
                          </p>
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="border-t border-border p-3 space-y-3">
                          {wo.description && (
                            <p className="text-xs text-muted-foreground">{wo.description}</p>
                          )}
                          {wo.images?.length > 0 && (
                            <div className="flex gap-2 flex-wrap">
                              {wo.images.map((url, idx) => (
                                <img
                                  key={idx}
                                  src={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                                  alt="attachment"
                                  className="w-16 h-16 object-cover rounded border border-border cursor-pointer hover:opacity-80"
                                  onClick={() => setSelectedImage(`${process.env.REACT_APP_BACKEND_URL}${url}`)}
                                />
                              ))}
                            </div>
                          )}
                          {wo.pic_name && (
                            <p className="text-xs text-muted-foreground">PIC: {wo.pic_name}</p>
                          )}

                          {wo.status === "CANCELLED" && (
                            <div className="text-xs rounded border border-border p-3 space-y-1">
                              <p>Alasan: {REASONS[wo.cancellation_reason_code] || wo.cancellation_reason_code}</p>
                              <p className="whitespace-pre-wrap">{wo.cancellation_reason}</p>
                              {wo.duplicate_of && <p>WO utama yang tetap berlaku: <strong>{wo.duplicate_of}</strong></p>}
                              <p>Oleh {wo.cancelled_by_name} · {wo.cancelled_at && new Date(wo.cancelled_at).toLocaleString("id-ID")}</p>
                            </div>
                          )}

                          {(canEdit(wo) || canCancel(wo)) && (
                            <div className="flex flex-wrap gap-2 border-t border-border pt-2">
                              {canEdit(wo) && <Button size="sm" variant="outline" disabled={!!acting || uploading} onClick={() => openCorrection(wo, "edit")}>Edit WO</Button>}
                              {canCancel(wo) && (
                                <Button size="sm" variant="outline" disabled={!!acting || uploading} onClick={() => openCorrection(wo, "cancel")}>Cancel WO</Button>
                              )}
                            </div>
                          )}

                          {correction?.id === wo.id && (
                            <div className="rounded border border-border p-3 space-y-3" role="region" aria-label={correction.mode === "edit" ? "Edit WO" : "Cancel WO"}>
                              <p className="text-sm font-medium">{correction.mode === "edit" ? "Koreksi Work Order" : "Konfirmasi pembatalan Work Order"}</p>
                              {correction.mode === "edit" ? (
                                <>
                                  {[['location', 'Lokasi'], ['title', 'Judul'], ['description', 'Deskripsi']].map(([field, label]) => (
                                    <label key={field} className="text-xs block">{label}{field !== "description" ? " *" : ""}
                                      <textarea rows={field === "description" ? 3 : 1} value={correction[field]} disabled={!!acting}
                                        onChange={(e) => setCorrection({ ...correction, [field]: e.target.value })}
                                        className="mt-1 w-full px-3 py-2 rounded-sm border border-border bg-background" />
                                    </label>
                                  ))}
                                  <div className="flex flex-wrap gap-2">
                                    {correction.images.map((url, index) => (
                                      <div key={`${url}-${index}`} className="flex items-center gap-1">
                                        <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt="Lampiran WO" className="w-12 h-12 object-cover rounded" />
                                        <Button size="sm" variant="ghost" disabled={!!acting} onClick={() => setCorrection({ ...correction, images: correction.images.filter((_, i) => i !== index) })}>Hapus lampiran</Button>
                                      </div>
                                    ))}
                                  </div>
                                  <label className="text-xs block">Tambah foto
                                    <input type="file" accept="image/*" disabled={uploading || !!acting} className="block mt-1"
                                      onChange={(e) => {
                                        const id = wo.id;
                                        handleFileUpload(e.target.files?.[0], (url) => setCorrection((prev) => prev?.id === id ? { ...prev, images: [...prev.images, url] } : prev));
                                      }} />
                                  </label>
                                </>
                              ) : (
                                <>
                                  <p className="text-xs text-muted-foreground">WO tidak dihapus. Pembatalan dan alasannya disimpan dalam riwayat, dan tidak dihitung sebagai pekerjaan selesai.</p>
                                  <label className="text-xs block">Jenis alasan *
                                    <select value={correction.reason_code} disabled={!!acting} onChange={(e) => setCorrection({ ...correction, reason_code: e.target.value })}
                                      className="mt-1 w-full px-3 py-2 rounded-sm border border-border bg-background">
                                      {Object.entries(REASONS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                                    </select>
                                  </label>
                                  {correction.reason_code === "DUPLICATE" && (
                                    <label className="text-xs block">Nomor WO utama yang tetap dikerjakan *
                                      <input value={correction.duplicate_of} disabled={!!acting} placeholder="WO-ABC123"
                                        onChange={(e) => setCorrection({ ...correction, duplicate_of: e.target.value })}
                                        className="mt-1 w-full px-3 py-2 rounded-sm border border-border bg-background" />
                                    </label>
                                  )}
                                </>
                              )}
                              <label className="text-xs block">Alasan {correction.mode === "edit" ? "koreksi" : "pembatalan"} *
                                <textarea value={correction.reason} maxLength={1000} disabled={!!acting} rows={2}
                                  onChange={(e) => setCorrection({ ...correction, reason: e.target.value })}
                                  className="mt-1 w-full px-3 py-2 rounded-sm border border-border bg-background" />
                              </label>
                              <div className="flex gap-2">
                                <Button size="sm" disabled={!!acting || uploading} onClick={saveCorrection}>{acting === wo.id ? "Menyimpan..." : correction.mode === "edit" ? "Simpan koreksi" : "Konfirmasi Cancel WO"}</Button>
                                <Button size="sm" variant="outline" disabled={!!acting || uploading} onClick={() => setCorrection(null)}>Kembali</Button>
                              </div>
                            </div>
                          )}

                          {wo.history?.length > 0 && (
                            <details className="border-t border-border pt-2 text-xs">
                              <summary className="cursor-pointer">Riwayat WO ({wo.history.length})</summary>
                              <div className="mt-2 space-y-3">
                                {[...wo.history].reverse().map((event, index) => (
                                  <div key={index} className="border-l-2 border-border pl-2 space-y-1">
                                    <p>{HISTORY_ACTIONS[event.action] || event.action} · {event.by_name} · {new Date(event.at).toLocaleString("id-ID")}</p>
                                    {event.reason && <p className="whitespace-pre-wrap">Alasan: {event.reason}</p>}
                                    {event.duplicate_of && <p>WO utama: {event.duplicate_of}</p>}
                                    {Object.entries(event.changes || {}).map(([field, change]) => (
                                      <p key={field} className="whitespace-pre-wrap">{FIELD_LABELS[field] || field}: {field === "images" ? `${(change.before || []).length} foto → ${(change.after || []).length} foto` : `${change.before || "(kosong)"} → ${change.after || "(kosong)"}`}</p>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}

                          {canManage && wo.status === "OPEN" && correction?.id !== wo.id && (
                            <div className="flex gap-2 items-center flex-wrap pt-2 border-t border-border">
                              <input
                                type="text"
                                placeholder="Nama PIC (optional)"
                                value={actionState[wo.id]?.pic_name || ""}
                                onChange={(e) => setAction(wo.id, { pic_name: e.target.value })}
                                className="flex-1 min-w-[140px] px-2.5 py-1.5 text-xs rounded-sm border border-border bg-background"
                                onClick={(e) => e.stopPropagation()}
                              />
                              <Button
                                size="sm"
                                disabled={acting === wo.id}
                                onClick={(e) => { e.stopPropagation(); handleStart(wo.id); }}
                              >
                                Start
                              </Button>
                            </div>
                          )}

                          {canManage && wo.status === "IN_PROGRESS" && correction?.id !== wo.id && (
                            <div className="space-y-2 pt-2 border-t border-border" onClick={(e) => e.stopPropagation()}>
                              <textarea
                                placeholder="Catatan penyelesaian (optional)"
                                value={actionState[wo.id]?.completion_notes || ""}
                                onChange={(e) => setAction(wo.id, { completion_notes: e.target.value })}
                                rows={2}
                                className="w-full px-2.5 py-1.5 text-xs rounded-sm border border-border bg-background"
                              />
                              <div className="flex items-center gap-2 flex-wrap">
                                <input
                                  type="file"
                                  accept="image/*"
                                  capture="environment"
                                  id={`wo-complete-photo-${wo.id}`}
                                  className="hidden"
                                  onChange={(e) => handleFileUpload(e.target.files?.[0], (url) =>
                                    setAction(wo.id, { completion_images: [...(actionState[wo.id]?.completion_images || []), url] })
                                  )}
                                />
                                <label
                                  htmlFor={`wo-complete-photo-${wo.id}`}
                                  className="text-[10px] px-2.5 py-1.5 rounded-sm border border-border text-muted-foreground cursor-pointer flex items-center gap-1.5"
                                >
                                  <Camera size={11} /> {uploading ? "Uploading..." : "Add Completion Photo (optional)"}
                                </label>
                                {(actionState[wo.id]?.completion_images || []).map((url, idx) => (
                                  <img
                                    key={idx}
                                    src={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                                    alt="completion"
                                    className="w-10 h-10 object-cover rounded border border-border cursor-pointer hover:opacity-80"
                                    onClick={() => setSelectedImage(`${process.env.REACT_APP_BACKEND_URL}${url}`)}
                                  />
                                ))}
                                <Button size="sm" disabled={acting === wo.id} onClick={() => handleComplete(wo.id)}>
                                  Mark Completed
                                </Button>
                              </div>
                            </div>
                          )}

                          {wo.status === "COMPLETED" && (
                            <div className="pt-2 border-t border-border space-y-2">
                              {wo.completion_notes && (
                                <p className="text-xs text-muted-foreground">Catatan: {wo.completion_notes}</p>
                              )}
                              {wo.completion_images?.length > 0 && (
                                <div className="flex gap-2 flex-wrap">
                                  {wo.completion_images.map((url, idx) => (
                                    <img
                                      key={idx}
                                      src={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                                      alt="completion"
                                      className="w-16 h-16 object-cover rounded border border-border cursor-pointer hover:opacity-80"
                                      onClick={() => setSelectedImage(`${process.env.REACT_APP_BACKEND_URL}${url}`)}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <Button
              variant="ghost"
              size="sm"
              className="absolute -top-10 right-0 text-white hover:text-white hover:bg-white/20"
              onClick={() => setSelectedImage(null)}
            >
              <X size={20} />
            </Button>
            <img
              src={selectedImage}
              alt="Full size"
              className="max-w-full max-h-[85vh] object-contain rounded"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </Layout>
  );
};

export default WorkOrders;
